import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';

import { PAGE } from '@/routes';

import HamburgerIcon from '@/assets/images/branding/hamburger.svg';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

import LandingButton from './LandingButton';
import { ALL_LANDING_NAV_ITEMS } from './landingNavItems';

const LANGUAGES = [
  { value: 'en', label: 'EN', flag: 'gb' },
  { value: 'es', label: 'SP', flag: 'es' },
  { value: 'it', label: 'IT', flag: 'it' }
];

const LandingMobileMenu = ({
  barClassName,
  activeSectionId,
  onNavigate,
  children
}: {
  barClassName: string;
  activeSectionId: string;
  onNavigate: (sectionId: string) => void;
  children: (menuButton: React.ReactNode) => React.ReactNode;
}) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [language, setLanguage] = useState(LANGUAGES[0].value);

  const menuButton = (
    <PopoverTrigger asChild>
      <button
        type="button"
        className={cn('inline-flex size-10 items-center justify-center lg:hidden', open && 'rounded-[12px] bg-white')}
        aria-label={open ? 'Close menu' : 'Open menu'}
      >
        {open ? (
          <X className="size-5 text-[#0A0A0A]" strokeWidth={1.75} />
        ) : (
          <img src={HamburgerIcon} alt="" className="size-10" />
        )}
      </button>
    </PopoverTrigger>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div className={barClassName}>{children(menuButton)}</div>
      </PopoverAnchor>
      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={12}
        avoidCollisions={false}
        className="w-[var(--radix-popper-anchor-width)] rounded-[24px] border-0 bg-white p-6 text-[#0A0A0A] shadow-[0px_16px_40px_0px_#0000001A] lg:hidden"
      >
        <nav>
          <ul className="flex flex-col items-center gap-5">
            {ALL_LANDING_NAV_ITEMS.map((item) => {
              const isActive = item.id === activeSectionId;

              return (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onNavigate(item.id);
                      setOpen(false);
                    }}
                    aria-current={isActive ? 'page' : undefined}
                    className="inline-flex items-center gap-2 text-base leading-6 tracking-[-0.32px] text-[#0A0A0A]"
                  >
                    {isActive && <span className="size-1.5 rounded-full bg-[#0A0A0A]" aria-hidden />}
                    {item.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        <LandingButton
          className="mt-8 w-full"
          innerClassName="w-full text-center"
          onClick={() => {
            setOpen(false);
            navigate(PAGE.WELCOME.path);
          }}
        >
          Try for Free
        </LandingButton>

        <div className="mt-6 flex items-center justify-center gap-3">
          {LANGUAGES.map((option) => {
            const selected = option.value === language;

            return (
              <button
                key={option.value}
                type="button"
                aria-pressed={selected}
                onClick={() => setLanguage(option.value)}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-[4px] border border-[#0013051A] bg-[#F5F5F5] p-1.5 text-base leading-6 tracking-[-0.32px] text-[#0A0A0A]',
                  selected && 'border-transparent bg-primary text-white'
                )}
              >
                <img
                  src={`https://flagcdn.com/w40/${option.flag}.png`}
                  alt=""
                  width={22}
                  height={16}
                  className="h-4 w-[22px] rounded-[1px] object-cover"
                />
                {option.label}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default LandingMobileMenu;
